/**
 * Theme utilities — recommender tracking (Cloudflare Worker backend)
 *
 * Migrated off Supabase: visitor identity is a client-generated UUID (no anonymous
 * auth / no supabase-js SDK), and all calls go to the Cloudflare Worker which serves
 * the same /functions/v1/<endpoint> paths.
 */

(function () {
  'use strict';

  // Configuration — Cloudflare Worker base (serves /functions/v1/* identically to the old API)
  const _base = 'https://bernard-recommender.bernard-legoff-29.workers.dev';
  const _api = _base + '/functions/v1';
  const _vk = 'rm_visitor_id'; // canonical visitor-id key (also read by the other recommender scripts)
  const _vp = '_v_';           // per-product return-visit marker (sessionStorage)
  const _mp = '_m_';           // customer-merged marker (localStorage)
  // legacy Supabase anon-auth storage keys — used once to recover a returning visitor's id
  const _legacyKeys = ['_sid', 'sb-rvzmfllwniqfnevesslb-auth-token'];

  // State
  let visitorId = null;
  let pageLoadTime = null;
  let currentProductId = null;
  let previousCart = null;
  let hasTrackedTimeOnPage = false;
  let seenProducts = {};
  let impressionObserver = null;
  let impressionsSent = false;

  // ============================================
  // Visitor Session Management (client UUID)
  // ============================================

  function uuidv4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // One-time migration: reuse the old Supabase anonymous-auth user id so returning
  // visitors keep their history (their preference vectors live under that id).
  function recoverLegacyVisitorId() {
    try {
      for (const key of _legacyKeys) {
        const stored = localStorage.getItem(key);
        if (!stored) continue;
        const d = JSON.parse(stored);
        const id = d && (d.user?.id || d.currentSession?.user?.id || d.session?.user?.id);
        if (id) return id;
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  async function getVisitorId() {
    if (visitorId) return visitorId;
    try {
      visitorId =
        localStorage.getItem(_vk) ||
        localStorage.getItem('visitorId') ||
        recoverLegacyVisitorId();
    } catch (e) { /* ignore */ }
    if (!visitorId) visitorId = uuidv4();
    try {
      localStorage.setItem(_vk, visitorId);
      localStorage.setItem('visitorId', visitorId); // back-compat for older scripts
    } catch (e) { /* ignore */ }
    return visitorId;
  }

  // ============================================
  // Interaction Tracking (Worker endpoint; product resolved server-side by shopify id)
  // ============================================

  async function trackInteraction(shopifyId, actionType, metadata = null) {
    const vid = await getVisitorId();
    if (!vid || !shopifyId) return null;
    try {
      const res = await fetch(_api + '/track-interaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: vid,
          shopifyId: String(shopifyId).replace('gid://shopify/Product/', ''),
          actionType: actionType,
          metadata: metadata,
        }),
      });
      return await res.json();
    } catch (error) {
      console.error('[Tracking] Interaction error:', error);
      return null;
    }
  }

  async function safeTrack(shopifyId, action, metadata = null) {
    try {
      await trackInteraction(shopifyId, action, metadata);
    } catch (error) {
      console.error('[Tracking] Error:', error);
    }
  }

  // ============================================
  // Impression Tracking — sendBeacon on unload
  // ============================================

  function markProductSeen(gid, context) {
    if (!seenProducts[context]) seenProducts[context] = new Set();
    seenProducts[context].add(gid);
  }

  function sendImpressions() {
    if (impressionsSent) return;
    let vid = visitorId;
    if (!vid) {
      try { vid = localStorage.getItem(_vk) || localStorage.getItem('visitorId') || recoverLegacyVisitorId(); } catch (e) { /* ignore */ }
    }
    const hasImpressions = Object.values(seenProducts).some((set) => set.size > 0);
    if (!vid || !hasImpressions) return;

    impressionsSent = true;
    const payload = { visitorId: vid, impressions: {} };
    Object.entries(seenProducts).forEach(([context, gidSet]) => {
      if (gidSet.size > 0) payload.impressions[context] = Array.from(gidSet);
    });
    const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
    navigator.sendBeacon(_api + '/track-impression', blob);
  }

  function setupImpressionTracking() {
    impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const gid = el.dataset.productGid;
          const context = el.dataset.impressionContext || 'browse';
          if (!gid) return;
          markProductSeen(gid, context);
          impressionObserver.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );
    document.querySelectorAll('[data-product-gid]').forEach((el) => impressionObserver.observe(el));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') sendImpressions();
    });
    window.addEventListener('pagehide', sendImpressions);
    window.addEventListener('beforeunload', sendImpressions);
  }

  // ============================================
  // Similar Products / Recommendations API
  // ============================================

  async function fetchSimilarProducts(productGid, options = {}) {
    const { limit = 12, facetWeights = null } = options;
    const body = { productGid, limit };
    if (facetWeights) body.facetWeights = facetWeights;
    try {
      const response = await fetch(_api + '/similar-products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return await response.json();
    } catch (error) {
      console.error('[Recommendations] Similar products error:', error);
      return null;
    }
  }

  async function fetchRecommendations(options = {}) {
    const vid = await getVisitorId();
    if (!vid) return null;
    const { limit = 24, mmrLambda = null, debiasStrength = null } = options;
    const body = { visitorId: vid, limit };
    if (mmrLambda !== null) body.mmrLambda = mmrLambda;
    if (debiasStrength !== null) body.debiasStrength = debiasStrength;
    try {
      const response = await fetch(_api + '/recommendations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return await response.json();
    } catch (error) {
      console.error('[Recommendations] Error:', error);
      return null;
    }
  }

  // ============================================
  // Product View / Time / Bounce
  // ============================================

  async function trackProductView() {
    const shopifyProductId = window.theme?.settings?.product_id;
    if (!shopifyProductId) return;
    currentProductId = String(shopifyProductId);
    pageLoadTime = Date.now();

    const visitKey = _vp + shopifyProductId;
    if (sessionStorage.getItem(visitKey)) {
      await safeTrack(currentProductId, 'return_visit');
    } else {
      await safeTrack(currentProductId, 'product_view');
      sessionStorage.setItem(visitKey, '1');
    }
  }

  async function trackTimeOnPage() {
    if (!currentProductId || !pageLoadTime || hasTrackedTimeOnPage) return;
    const timeSpent = Date.now() - pageLoadTime;
    if (timeSpent > 30000) {
      hasTrackedTimeOnPage = true;
      await safeTrack(currentProductId, 'time_on_page', { duration_ms: timeSpent });
    }
  }

  async function trackBounce() {
    if (!currentProductId || !pageLoadTime) return;
    const timeSpent = Date.now() - pageLoadTime;
    if (timeSpent < 5000) await safeTrack(currentProductId, 'bounce', { duration_ms: timeSpent });
  }

  function setupTimeTracking() {
    if (!currentProductId) return;
    setTimeout(trackTimeOnPage, 30000);
    window.addEventListener('beforeunload', async () => {
      await trackTimeOnPage();
      await trackBounce();
    });
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'hidden') await trackTimeOnPage();
    });
  }

  // ============================================
  // Cart Tracking (Pinnacle theme events)
  // ============================================

  function setupCartTracking() {
    document.addEventListener('cart:item-added', async (evt) => {
      const product = evt.detail?.product;
      if (!product?.id) return;
      await safeTrack(String(product.id), 'add_to_cart', {
        variant_id: product.variant_id,
        quantity: product.quantity,
      });
      attachVisitorToCart();
    });

    document.addEventListener('line-item:change:end', async (evt) => {
      const currentCart = evt.detail?.cart;
      if (previousCart && currentCart) {
        for (const prevItem of previousCart.items || []) {
          const currentItem = currentCart.items?.find((i) => i.id === prevItem.id);
          if (!currentItem || currentItem.quantity < prevItem.quantity) {
            await safeTrack(String(prevItem.product_id), 'remove_from_cart');
          }
        }
      }
      previousCart = currentCart ? JSON.parse(JSON.stringify(currentCart)) : null;
    });

    fetchCurrentCart();
  }

  async function fetchCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      previousCart = await response.json();
    } catch (error) {
      console.error('[Tracking] Failed to fetch cart:', error);
    }
  }

  function setupImageZoomTracking() {
    document.addEventListener('glightbox:open', async () => {
      if (currentProductId) await safeTrack(currentProductId, 'image_zoom');
    });
  }

  function setupSearchTracking() {
    document.addEventListener('click', async (e) => {
      const searchResult = e.target.closest('.predictive-search__result-link, .search-result-item a, [data-search-result]');
      if (!searchResult) return;
      const href = searchResult.getAttribute('href');
      const productId = searchResult.dataset?.productId;
      if (productId) await safeTrack(productId, 'search_click', { search_url: href });
    });
  }

  // ============================================
  // Customer Identification (Worker merge endpoint)
  // ============================================

  async function identifyCustomer() {
    const vid = await getVisitorId();
    if (!vid || !window.shopifyCustomer) return;
    const mergedKey = _mp + window.shopifyCustomer.id;
    if (localStorage.getItem(mergedKey)) return;
    try {
      const res = await fetch(_api + '/merge-visitor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: vid,
          customerId: String(window.shopifyCustomer.id),
          email: window.shopifyCustomer.email,
        }),
      });
      const d = await res.json();
      if (d && d.success) {
        // merge may return a canonical visitor id (when the customer already had one)
        if (d.visitorId && d.visitorId !== vid) {
          visitorId = d.visitorId;
          try {
            localStorage.setItem(_vk, visitorId);
            localStorage.setItem('visitorId', visitorId);
          } catch (e) { /* ignore */ }
        }
        localStorage.setItem(mergedKey, '1');
      }
    } catch (error) {
      console.error('[Tracking] Customer identification failed:', error);
    }
  }

  // ============================================
  // Cart Attributes (for checkout attribution)
  // ============================================

  async function attachVisitorToCart() {
    const vid = await getVisitorId();
    if (!vid) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { visitor_id: vid } }),
      });
    } catch (error) {
      console.error('[Tracking] Failed to attach visitor to cart:', error);
    }
  }

  // ============================================
  // Initialization
  // ============================================

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    setupCartTracking();
    setupImageZoomTracking();
    setupSearchTracking();
    setupImpressionTracking();

    getVisitorId().then(() => {
      if (window.theme?.settings?.product_id) {
        trackProductView();
        setupTimeTracking();
      }
      if (window.shopifyCustomer) identifyCustomer();
    });

    document.addEventListener('cart-drawer:open', attachVisitorToCart);

    console.log('[Tracking] Initialized (Cloudflare)');
  }

  init();

  // Public interface (consumed by the other recommender scripts)
  window._rm = {
    gv: getVisitorId,
    t: safeTrack,
    seen: () => seenProducts,
    flush: sendImpressions,
    sp: fetchSimilarProducts,
    rec: fetchRecommendations,
  };
})();
